/**
 * Die Stimme der Morgenmeditation, getrennt von der Alltagsstimme.
 * (24.09.2026, Ticket 2556)
 *
 * Martin nach der ersten Meditation (Ticket 2549): *„die Stimme ist zu wach,
 * zu schnell, wirkt eher gestresst — für eine Tiefenentspannung fehlt Ruhe
 * und Tiefe, auch wenn das Sprechtempo gedrosselt wird."*
 *
 * ## Der Befund
 *
 * Der Nachtlauf las mit `vesperaStimme()`, also mit der Vorlese-Stimme des
 * Alltags, bei Martin `soniox:Nathan`, und ohne `speed` (Soniox-Vorgabe 1,0).
 * Stand vom 24.09. (data/morgenmeditation/1-2026-09-24.json): 5.835 Zeichen
 * in 383,7 s, das sind 15,2 Zeichen je Sekunde. Gemessen wurden sonst 13 bis
 * 15,5 (server/morgen-meditation.ts); die Meditation lag also am schnellen
 * Ende dessen, was Vespera überhaupt spricht.
 *
 * ## Die Wahl, nur aus Belegen
 *
 * Soniox beschreibt seine Stimmen selbst: Die API-Referenz „Get TTS models"
 * (soniox.com/docs/api-reference/tts/get_tts_models) zeigt eine
 * Beispielantwort mit 28 Stimmen samt Feld `description`. Von Martins vier
 * Soniox-Stimmen (am 09.09. nach dem Hören gewählt) stehen dort zwei:
 *
 *   Daniel: „A rich, steady male voice with a polished tone, controlled
 *            pacing, and a reassuring presence that feels confident and
 *            mature."
 *   Grace:  „A gentle, soothing female voice with soft clarity, unhurried
 *            pacing, and a reassuring tone that feels kind, calm, and
 *            comforting."
 *
 * Nathan und Iris kommen dort nicht vor. „Klar, tief" bei Nathan in
 * frontend/lib/settings.ts ist eine eigene Zuschreibung ohne Quelle. Belegt
 * ruhig sind also Daniel (voll, gleichmäßig, kontrolliertes Tempo,
 * beruhigend, reif) und Grace (sanft, beruhigend, ohne Eile).
 *
 * ⚠️ Die Beispielantwort gehört zum Modell tts-rt-v1. Vespera spricht mit
 *    tts-rt-v2, das laut Soniox voll kompatibel ist, nur der Modellname
 *    wechselt (server/soniox-tts.ts, SONIOX_TTS_MODEL). Daniel spricht dort
 *    nachweislich (Protokoll 16.09., voice-preview 200).
 *
 * ⚠️ Gleiches Geschlecht wie die Alltagsstimme: Wer eine Frauenstimme gewählt
 *    hat, bekommt Grace, alle anderen Daniel. Ein plötzlicher Wechsel des
 *    Geschlechts war schon einmal eine Beschwerde (25.07., „plötzlich wieder
 *    eine Frauenstimme", siehe STIMMEN_UMZUG in frontend/lib/settings.ts).
 *
 * ⚠️ Martins Soniox-Klon ist NICHT in der Liste, obwohl er ihn am 24.09. als
 *    bevorzugte Spur genannt hat. Der Klon (`soniox:2cf4558a-…`, angelegt am
 *    22.07., am 24.07. auf seinen Wunsch aus der Auswahl genommen, Commit
 *    c8e83409) entstand VOR tts-rt-v2 (11.08.). Laut Soniox-Doku
 *    („Voice cloning") wird eine Stimme nur für die Modelle vorbereitet, die
 *    es bei ihrer Anlage gab; ein späteres Modell meldet `not_computed`, bis
 *    man sie neu berechnen lässt. Eine Neuberechnung steht nirgends im
 *    Verlauf. Ob er auf v2 spricht, zeigt nur ein Abruf mit Schlüssel
 *    (GET /v1/voices). Erst wenn der „ready" meldet, gehört er als EINE Zeile
 *    hierher, und nur für das Konto, dem er gehört.
 *
 * ## Das Tempo
 *
 * Soniox nimmt `speed` von 0,7 bis 1,3 (Doku „Speech speed"). Die Doku rät,
 * zuerst eine Stimme zu wählen, deren eigenes Tempo passt, und `speed` nur
 * maßvoll zu ändern, weil es den Rhythmus glätten kann.
 *
 * 24.09.2026, Ticket 2569: zurück auf normal. Martin nach der Hörprobe bei
 * 0,8: *„Die Stimme ist toll, aber ich glaube, es braucht die normale
 * Sprechgeschwindigkeit … die Sprechgeschwindigkeit klingt wie ein
 * Betrunkener."* Genau davor warnt die Doku: `speed` dehnt jede Silbe, nicht
 * nur die Pausen. Die Ruhe kommt jetzt aus echter Stille zwischen den
 * Absätzen (server/meditation-ton.ts), das Tempo bleibt das der Stimme.
 */

interface MeditationsStimme {
  id: string;
  label: string;
  gender: "female" | "male";
}

/*
 * 24.09.2026, Martin: *„Füge diese Stimme bei Soniox hinzu. Diese wird als
 * besonders ruhig dargestellt für die Meditationen … ‚Konrad'. Zum Teil
 * könnte man die Sprechgeschwindigkeit etwas herunterfahren."*
 *
 * Nachgesehen in GET https://api.soniox.com/v1/tts-models, Modell tts-rt-v2
 * (dem Modell, das Vespera spricht), Feld `description`:
 *
 *   Konrad:  „A tender German male voice with a soft, lulling manner made
 *            for reading to children."
 *   Marlene: „A serene German female voice with quiet authority and
 *            understated feeling, at home in nature documentaries."
 *
 * Beide sind DEUTSCHE Muttersprach-Stimmen — anders als Daniel und Grace, die
 * Soniox ohne Sprache beschreibt. Darum sind sie jetzt die Vorgabe. Gemessen
 * am 24.09. (569 Zeichen, erfundener Meditationstext): Konrad bei 0,8 in
 * 57,6 s = 9,9 Zeichen/s, Konrad 0,85 in 54,9 s, Daniel 0,85 in 53,6 s.
 * Weitere ruhige deutsche Soniox-Stimmen, falls Martin wechseln will:
 * Jonas, Florian (m), Annika (w).
 */
/**
 * Stimmen, die es NUR für die Meditation gibt. Seit 15.10.2026 leer: Martin
 * wollte Konrad und Marlene auch zum Vorlesen (VOICE_OPTIONS).
 */
export const NUR_MEDITATION: readonly string[] = [];

/** Was in „Stimme der Meditation" zur Wahl steht. */
export const MEDITATIONS_STIMMEN: readonly MeditationsStimme[] = [
  { id: "soniox:Konrad", label: "Konrad", gender: "male" },
  { id: "soniox:Marlene", label: "Marlene", gender: "female" },
  { id: "soniox:Grace", label: "Grace", gender: "female" },
  { id: "soniox:Daniel", label: "Daniel", gender: "male" },
  { id: "soniox:Iris", label: "Iris", gender: "female" },
  { id: "soniox:Nathan", label: "Nathan", gender: "male" },
];

/** Die Vorgabe je Geschlecht der Alltagsstimme, Belege oben. */
export const MEDITATIONS_VORGABE = {
  weiblich: "soniox:Marlene",
  sonst: "soniox:Konrad",
} as const;

/**
 * Sprechtempo der Meditation (Soniox `speed`; 1 = normal).
 *
 * 24.09.2026: erst 0,85 (Ticket 2556), dann 0,8 auf Martins Wunsch (pre.3249),
 * nach der Hörprobe zurück auf 1 (Ticket 2569, Kopf dieser Datei).
 * sonioxTempo(1) ist undefined; die Anfrage an Soniox trägt dann KEIN
 * speed-Feld, und die Stückgrenze ist wieder die volle (sonioxDecke: 1.800
 * Zeichen). Konrad im Normaltempo, gemessen am 24.09. (Hörprobe, derselbe
 * erfundene Text wie bei der Messung oben, in sechs Absätze geteilt: 564
 * Zeichen in 44,5 s gesprochen, dazu 18 s Stille): 12,7 Zeichen/s. 1.800
 * Zeichen sind damit rund 142 s, 38 s unter der 180-Sekunden-Decke. Die
 * Leitung für ein eigenes Tempo bleibt (Stand, Schlüssel, Dauerprüfung),
 * falls es je wieder gebraucht wird.
 */
export const MEDITATIONS_TEMPO = 1;

/**
 * Ist diese Alltagsstimme eine Frauenstimme?
 *
 * ⚠️ Deckt die Stimmen mit `gender: "female"` aus frontend/lib/settings.ts
 *    ab (VOICE_OPTIONS, GEMINI_READ_VOICES, GOOGLE_NARRATOR_VOICES) plus die
 *    umgezogenen Soniox-Namen aus STIMMEN_UMZUG. Der Server kennt diese
 *    Listen nicht; tests/shared/meditations-stimme.test.ts hält beide
 *    beieinander. Ein Klon oder eine unbekannte Stimme zählt nicht als
 *    Frauenstimme und bekommt Daniel.
 */
const FRAUENSTIMME =
  /^(?:soniox:(?:Grace|Iris|Maya|Emma|Marlene)|de-DE-Chirp3-HD-(?:Aoede|Kore|Leda|Zephyr|Achernar)|EXAVITQu4vr4xnSDxMaL)$/;

export function istFrauenstimme(voiceId: string | null | undefined): boolean {
  return typeof voiceId === "string" && FRAUENSTIMME.test(voiceId.trim());
}

export function istMeditationsStimme(v: unknown): v is string {
  return typeof v === "string" && MEDITATIONS_STIMMEN.some((s) => s.id === v);
}

/** Die Vorgabe, wenn das Konto nichts gewählt hat. */
export function meditationsVorgabe(alltagsStimme: string | null | undefined): string {
  return istFrauenstimme(alltagsStimme) ? MEDITATIONS_VORGABE.weiblich : MEDITATIONS_VORGABE.sonst;
}

/**
 * Welche Stimme liest die Meditation dieses Kontos?
 *
 * Gewählt und in der Liste: die gewählte. Sonst die Vorgabe zum Geschlecht
 * der Alltagsstimme. Ein unbekannter Wert (alte Stimme, Tippfehler) fällt auf
 * die Vorgabe, nie auf die Alltagsstimme.
 */
export function meditationsStimmeAus(
  gewaehlt: unknown,
  alltagsStimme: string | null | undefined,
): string {
  return istMeditationsStimme(gewaehlt) ? gewaehlt : meditationsVorgabe(alltagsStimme);
}

/** Der Name einer Stimme aus der Liste, für die Anzeige. */
function nameVon(id: string): string {
  return MEDITATIONS_STIMMEN.find((s) => s.id === id)?.label ?? id;
}

/**
 * Was „Automatisch" bedeutet, in Worten, aus denselben Konstanten wie die
 * Regel. Die Einstellungsseite schreibt die Namen so nicht ein zweites Mal.
 */
export function vorgabeBeschreibung(): string {
  return `${nameVon(MEDITATIONS_VORGABE.sonst)}, zu einer Frauenstimme ${nameVon(MEDITATIONS_VORGABE.weiblich)}`;
}

/*
 * ═══════════════════════════════════════════════════════════════════════
 * ELEVENLABS v4 ALS ALTERNATIVE — NUR FÜR DIE MEDITATION (07.10.2026)
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Martin, 07.10.2026: *„In erster Linie möchte ich alternativ NUR FÜR DIE
 * MEDITATION die ElevenLabs V4 haben."*
 *
 * Ein eigenes Feld NEBEN `meditationVoiceId`, nicht an seiner Stelle:
 *   - `meditationVoiceId` bleibt die Soniox-Stimme der Meditation. Sie ist
 *     zugleich der Rückfall, wenn ElevenLabs nicht spricht (Guthaben,
 *     Schlüssel, unbekanntes Modell) — „zurück auf die bisherige Stimme".
 *   - `meditationElevenStimme` ist die ElevenLabs-Stimme. Steht sie da, liest
 *     sie die Meditation mit Eleven v4 (server/eleven-v4.ts,
 *     MEDITATION_ELEVEN_MODELL). `null` schaltet zurück auf Soniox.
 *
 * ⚠️ NUR die Meditation: Vorlesen, Antworten, Spiegel und Biografie lesen
 *    dieses Feld nicht.
 * ⚠️ NUR Konto 1: ElevenLabs bleibt für fremde Konten zu, wie seit dem
 *    16.09.2026 (frontend/lib/settings-view.ts, „beim User nur Soniox und
 *    Gemini"). Gespeichert wird das Feld für jedes Konto; WIRKEN tut es nur
 *    bei Konto 1 (meditationElevenStimmeAus).
 */

/** Der Schlüssel in den Einstellungen des Kontos. */
export const MEDITATION_ELEVEN_FELD = "meditationElevenStimme";

/** Nur dieses Konto darf die Meditation mit ElevenLabs hören (SUPERUSER_ID). */
const MEDITATION_ELEVEN_KONTO = 1;

/**
 * Die ElevenLabs-Stimmen, die die App schon kennt (frontend/lib/settings.ts,
 * VOICE_OPTIONS) — als Vorschlag für die Einstellungsseite. Gewählt werden
 * darf auch eine eigene Stimme des Kontos (customVoices); geprüft wird die
 * Form der Kennung, nicht die Liste.
 */
export const MEDITATION_ELEVEN_STIMMEN: readonly { id: string; label: string }[] = [
  { id: "hjWcVMU82C7uXy86PA2K", label: "Deine Stimme" },
  { id: "EXAVITQu4vr4xnSDxMaL", label: "Sarah" },
  { id: "JBFqnCBsd6RMkjVDRZzb", label: "George" },
];

/**
 * Sieht das aus wie eine ElevenLabs-Stimmkennung? Rein.
 * ElevenLabs-IDs sind 20 Zeichen aus Buchstaben und Ziffern
 * (tools/tts-render.ts, isGoogleVoice) — nichts anderes geht an die Adresse.
 */
export function istElevenStimmKennung(v: unknown): v is string {
  return typeof v === "string" && /^[A-Za-z0-9]{20}$/.test(v);
}

/** Darf dieses Konto ElevenLabs für die Meditation wählen? Rein. */
export function meditationElevenWaehlbar(userId: number | null | undefined): boolean {
  return userId === MEDITATION_ELEVEN_KONTO;
}

/**
 * Die ElevenLabs-Stimme, die die Meditation dieses Kontos liest — oder
 * `null` (dann Soniox wie bisher). Fremde Konten und ungültige Werte: `null`.
 */
export function meditationElevenStimmeAus(
  wert: unknown,
  userId: number | null | undefined,
): string | null {
  return meditationElevenWaehlbar(userId) && istElevenStimmKennung(wert) ? wert : null;
}
